<template>
  <v-card class="queues-health-card" elevation="1" style="border-radius: 12px;">
    <v-card-title class="pb-0 d-block">
      <div class="d-flex align-center">
        <span class="text-h6">Files d'attente</span>
        <v-spacer />
      </div>
      <div class="subtitle text-caption text--secondary mt-1">Santé des {{ queues.length }} files</div>
      <v-divider class="mt-2" />
    </v-card-title>

    <v-card-text class="pt-2">
      <div class="queues-list" role="list">
        <div
          v-for="q in sortedQueues"
          :key="q.name"
          class="queue-line"
          role="listitem"
        >
          <!-- Header line: name + heartbeat -->
          <div class="queue-head">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="queue-name" v-bind="attrs" v-on="on">{{ q.name }}</div>
              </template>
              <span>{{ q.name }}</span>
            </v-tooltip>
          </div>

          <!-- KPI tiles: 3 equal tiles in one row; wrap 2x2 under ~1024px -->
          <div class="tiles">
            <div class="tile tile-workers" :class="tileChangedClass(q.name, 'totalWorkers')">
              <div class="tile-value tabular">{{ q.totalWorkers }}</div>
              <div class="tile-label">Workers</div>
            </div>
            <div class="tile tile-busy" :class="tileChangedClass(q.name, 'busyWorkers')">
              <div class="tile-value tabular busy-text">{{ q.busyWorkers }}</div>
              <div class="tile-label">Occupés</div>
            </div>
            <div class="tile tile-idle" :class="tileChangedClass(q.name, 'idleWorkers')">
              <div class="tile-value tabular idle-text">{{ q.idleWorkers }}</div>
              <div class="tile-label">Libres</div>
            </div>
          </div>
        </div>

        <div v-if="queues && queues.length === 0" class="empty-state text-center py-4">Aucune file</div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'QueuesHealthCard',
  props: {
    queues: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      prevMap: {},
      changed: {},
    };
  },
  computed: {
    sortedQueues() {
      const list = [...this.queues];
      // Sort alphabetically by queue name (fr-FR), stable and case-insensitive
      return list.sort((a, b) => {
        const an = (a && a.name ? a.name : '').toString();
        const bn = (b && b.name ? b.name : '').toString();
        return an.localeCompare(bn, 'fr-FR', { sensitivity: 'base' });
      });
    }
  },
  watch: {
    queues: {
      handler(newVal) {
        const nowMap = {};
        for (const q of newVal) {
          const key = q.name;
          const prev = this.prevMap[key] || {};
          const fields = ['totalWorkers', 'busyWorkers', 'idleWorkers'];
          for (const f of fields) {
            if (prev[f] !== undefined && prev[f] !== q[f]) {
              this.$set(this.changed, `${key}:${f}`, true);
              setTimeout(() => this.$delete(this.changed, `${key}:${f}`), 500);
            }
          }
          const prevUtil = prev.__util;
          const util = this.getUtilizationPercentage(q);
          if (prevUtil !== undefined && prevUtil !== util) {
            this.$set(this.changed, `${key}:util`, true);
            setTimeout(() => this.$delete(this.changed, `${key}:util`), 500);
          }
          nowMap[key] = {
            totalWorkers: q.totalWorkers,
            busyWorkers: q.busyWorkers,
            idleWorkers: q.idleWorkers,
            inflightJobs: q.inflightJobs,
            __util: util
          };
        }
        this.prevMap = nowMap;
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    tileChangedClass(name, field) {
      return this.changed[`${name}:${field}`] ? 'tile-changed' : '';
    },
    getUtilizationPercentage(queue) {
      if (!queue.totalWorkers || queue.totalWorkers === 0) return 0;
      return Math.round((queue.busyWorkers / queue.totalWorkers) * 100);
    },
    statusColor(q) {
      const p = this.getUtilizationPercentage(q);
      const occupiedPct = p;
      if (occupiedPct >= 90) return 'error--text';
      if (occupiedPct >= 60) return 'warning--text';
      return 'success--text';
    }
  }
};
</script>

<style lang="scss">
.queues-health-card {
  height: 100%;
  display: flex;
  flex-direction: column;

  .v-card-text {
    flex: 1;
    overflow-y: auto;
    padding-top: 8px !important;
  }
}

.subtitle { line-height: 1; }

.queues-list { overflow-y: auto; }

.queue-line {
  position: relative;
  padding: 8px 0 0;
  margin-bottom: 22px; // spacing between queues 20–24px
}

.queue-head {
  display: flex;
  align-items: center;
  position: relative;
}

.queue-name {
  font-weight: 600;
  font-size: 16px;
  max-width: calc(100% - 24px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}


.tiles {
  display: flex;
  gap: 16px; // space between tiles
  margin-top: 10px;
  flex-wrap: nowrap;
}

.tile {
  flex: 1 1 0;
  min-width: 120px;
  height: 64px; // reduced to fit
  border-radius: 12px;
  padding: 12px;
  border: 1px solid #f0f0f0;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.tile-icon { font-size: 16px; line-height: 1; opacity: 0.85; }
.tile-value { font-size: 17px; font-weight: 600; margin-top: 6px; color: #111827; }
.tile-label { font-size: 12px; color: #6b7280; margin-top: 2px; }

// Light pastel backgrounds and colored values
.tile-workers { background: #f8f9fb; }
.tile-busy { background: #eef5ff; }
.tile-idle { background: #edf9f3; }
.busy-text { color: #2563eb; }
.idle-text { color: #059669; }

.tabular { font-variant-numeric: tabular-nums; }

.tile-changed { animation: tileFlash 0.5s ease; }
@keyframes tileFlash { 0% { box-shadow: inset 0 0 0 100vmax rgba(59,130,246,0.12);} 100% { box-shadow: inset 0 0 0 0 rgba(0,0,0,0);} }

.empty-state { color: #6b7280; }

// Responsiveness: below ~1024px allow wrap 2x2 while keeping order
@media (max-width: 1024px) {
  .tiles { flex-wrap: wrap; }
  .tile { flex: 1 1 calc(50% - 8px); }
}

@media (max-width: 768px) {
  .queues-health-card { height: 100%; }
}
</style>